import React, {useEffect, useState} from 'react';
import styled from 'styled-components';
import {$get, $post, $delete} from '../lib/requests';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {BookCard} from './BookCard';
import {Book} from 'shared';

const Container = styled.div`
  flex-grow: 1;
  height: calc(100vh - 65px);
  padding: 24px;
  overflow: auto;
  font-family: 'Roboto', sans-serif;
  box-sizing: border-box;
  background-color: #f8fafd; // MD3 surface
  color: #1d1b20; // MD3 on-surface
`;

const PageTitle = styled.h1`
  font-size: 32px;
  font-weight: 400;
  margin: 0 0 32px 0;
  color: #1d1b20;
`;

const Accordion = styled.details`
  background-color: #f3f4f9; // Surface container low
  border-radius: 16px;
  margin-bottom: 16px;
  overflow: hidden;
  transition: box-shadow 0.2s;
  border: 1px solid transparent;

  &[open] {
    background-color: #f8fafd; // Surface
    border-color: #c4c7c5; // Outline variant
    box-shadow:
      0px 1px 3px 1px rgba(0, 0, 0, 0.15),
      0px 1px 2px 0px rgba(0, 0, 0, 0.3);
  }
`;

const AccordionSummary = styled.summary`
  padding: 16px 24px;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 18px;
  font-weight: 500;
  list-style: none;
  color: #1d1b20;

  &::-webkit-details-marker {
    display: none;
  }

  &:hover {
    background-color: rgba(29, 27, 32, 0.08); // on-surface state layer
  }
`;

const AccordionDetails = styled.div`
  padding: 8px 24px 24px 24px;
  border-top: 1px solid #c4c7c5;
`;

const FormRow = styled.div`
  display: flex;
  gap: 16px;
  margin-bottom: 24px;
  align-items: flex-end;
  flex-wrap: wrap;
`;

const MergeForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 16px;
  align-items: flex-start;
  width: 100%;
  max-width: 600px;
`;

const InputGroup = styled.div<{$fullWidth?: boolean}>`
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: ${props => (props.$fullWidth ? '100%' : 'auto')};
`;

const InputLabel = styled.label`
  font-size: 12px;
  color: #49454f; // MD3 on-surface-variant
  font-weight: 500;
`;

const TextField = styled.input`
  padding: 12px 16px;
  border-radius: 4px;
  border: 1px solid #79747e; // MD3 outline
  font-size: 16px;
  color: #1d1b20;
  background-color: transparent;
  outline: none;
  min-width: 250px;
  width: 100%;
  box-sizing: border-box;

  &:focus {
    border-color: #0b57d0;
    border-width: 2px;
    padding: 11px 15px;
  }
`;

const SelectBox = styled.select`
  padding: 12px 16px;
  border-radius: 4px;
  border: 1px solid #79747e;
  font-size: 16px;
  color: #1d1b20;
  background-color: transparent;
  outline: none;
  min-width: 200px;
  width: 100%;
  box-sizing: border-box;

  &:focus {
    border-color: #0b57d0;
    border-width: 2px;
    padding: 11px 15px;
  }
`;

const Md3FilledButton = styled.button`
  background: #0b57d0; // MD3 Primary
  color: #ffffff; // On primary
  border: none;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  padding: 10px 24px;
  border-radius: 100px;
  height: 40px;
  display: inline-flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: #0842a0; // Hover
    box-shadow:
      0px 1px 3px 1px rgba(0, 0, 0, 0.15),
      0px 1px 2px 0px rgba(0, 0, 0, 0.3);
  }
`;

const Md3OutlinedButton = styled.button<{$error?: boolean}>`
  background: transparent;
  border: 1px solid ${props => (props.$error ? '#b3261e' : '#79747e')};
  color: ${props => (props.$error ? '#b3261e' : '#0b57d0')};
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  padding: 8px 16px;
  border-radius: 100px;
  display: inline-flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background-color: ${props =>
      props.$error ? 'rgba(179, 38, 30, 0.08)' : 'rgba(11, 87, 208, 0.08)'};
  }
`;

const TableContainer = styled.div`
  border: 1px solid #c4c7c5;
  border-radius: 12px;
  overflow: hidden;
  background-color: #f8fafd;
  margin-top: 24px;
`;

const StyledTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
`;

const TableHead = styled.thead`
  background-color: #f3f4f9; // Surface container low
`;

const TableRow = styled.tr`
  border-bottom: 1px solid #c4c7c5;
  &:last-child {
    border-bottom: none;
  }
`;

const TableHeader = styled.th`
  text-align: left;
  padding: 12px 16px;
  font-weight: 600;
  color: #1d1b20;

  &:last-child {
    text-align: right;
  }
`;

const TableCell = styled.td`
  padding: 12px 16px;
  color: #49454f;

  &:last-child {
    text-align: right;
  }
`;

const CenteredTableCell = styled(TableCell)`
  text-align: center;
  padding: 32px;
`;

const FormActionRow = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-top: 8px;
`;

const BooksGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  margin: -8px;
`;

const EmptyMessage = styled.div`
  padding: 32px;
  color: #49454f;
  width: 100%;
  text-align: center;
`;

const MessageText = styled.span`
  margin-top: 8px;
  color: #0b57d0;
  font-weight: 500;
`;

export default function AdminDashboard() {
  const [users, setUsers] = useState<{email: string; role: string}[]>([]);
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('VOLUNTEER');

  const [mergeType, setMergeType] = useState('author');
  const [primaryName, setPrimaryName] = useState('');
  const [secondaryNamesStr, setSecondaryNamesStr] = useState('');
  const [mergeMessage, setMergeMessage] = useState('');

  const [assignedBooks, setAssignedBooks] = useState<Book[]>([]);

  useEffect(() => {
    loadUsers();
    loadAssignedBooks();
  }, []);

  async function loadAssignedBooks() {
    try {
      const res = await $get<{books?: Book[]}>(
        '/api/books?assignedTo=*&pageSize=1000'
      );
      if (res && res.books) {
        setAssignedBooks(res.books);
      }
    } catch (err) {
      console.error('Error loading assigned books:', err);
    }
  }

  async function loadUsers() {
    try {
      const res =
        await $get<{email: string; role: string}[]>('/api/admin/users');
      if (Array.isArray(res)) {
        setUsers(res);
      } else {
        setUsers([]);
        console.error('Failed to load users:', res);
      }
    } catch (err) {
      setUsers([]);
      console.error('Error loading users:', err);
    }
  }

  async function handleAddUser(e: React.FormEvent) {
    e.preventDefault();
    if (!email) return;
    await $post('/api/admin/users', {email, role});
    setEmail('');
    loadUsers();
  }

  async function handleRemoveUser(emailToRemove: string) {
    if (window.confirm(`Remove ${emailToRemove}?`)) {
      await $delete(`/api/admin/users/${emailToRemove}`);
      loadUsers();
    }
  }

  async function handleMerge(e: React.FormEvent) {
    e.preventDefault();
    const secondaryNames = secondaryNamesStr
      .split(',')
      .map(s => s.trim())
      .filter(s => s);
    if (!primaryName || secondaryNames.length === 0) return;

    try {
      setMergeMessage('Merging...');
      await $post('/api/admin/merge-metadata', {
        type: mergeType,
        primaryName,
        secondaryNames,
      });
      setMergeMessage('Merged successfully!');
      setPrimaryName('');
      setSecondaryNamesStr('');
    } catch (err) {
      setMergeMessage('Merge failed.');
    }
  }

  return (
    <Container>
      <PageTitle>Admin Dashboard</PageTitle>

      <Accordion open>
        <AccordionSummary>
          User Management
          <ExpandMoreIcon />
        </AccordionSummary>
        <AccordionDetails>
          <form onSubmit={handleAddUser}>
            <FormRow>
              <InputGroup>
                <InputLabel>Email</InputLabel>
                <TextField
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  required
                />
              </InputGroup>

              <InputGroup>
                <InputLabel>Role</InputLabel>
                <SelectBox value={role} onChange={e => setRole(e.target.value)}>
                  <option value="VOLUNTEER">Volunteer</option>
                  <option value="AUTHORISED_USER">Authorised User</option>
                </SelectBox>
              </InputGroup>

              <Md3FilledButton type="submit">Add / Update User</Md3FilledButton>
            </FormRow>
          </form>

          <TableContainer>
            <StyledTable>
              <TableHead>
                <TableRow>
                  <TableHeader>Email</TableHeader>
                  <TableHeader>Role</TableHeader>
                  <TableHeader>Actions</TableHeader>
                </TableRow>
              </TableHead>
              <tbody>
                {users.map(u => (
                  <TableRow key={u.email}>
                    <TableCell>{u.email}</TableCell>
                    <TableCell>{u.role}</TableCell>
                    <TableCell>
                      <Md3OutlinedButton
                        $error
                        onClick={() => handleRemoveUser(u.email)}
                      >
                        Remove
                      </Md3OutlinedButton>
                    </TableCell>
                  </TableRow>
                ))}
                {users.length === 0 && (
                  <TableRow>
                    <CenteredTableCell colSpan={3}>
                      No users found.
                    </CenteredTableCell>
                  </TableRow>
                )}
              </tbody>
            </StyledTable>
          </TableContainer>
        </AccordionDetails>
      </Accordion>

      <Accordion>
        <AccordionSummary>
          Merge Metadata
          <ExpandMoreIcon />
        </AccordionSummary>
        <AccordionDetails>
          <MergeForm onSubmit={handleMerge}>
            <InputGroup $fullWidth>
              <InputLabel>Type</InputLabel>
              <SelectBox
                value={mergeType}
                onChange={e => setMergeType(e.target.value)}
              >
                <option value="author">Author</option>
                <option value="category">Category</option>
              </SelectBox>
            </InputGroup>

            <InputGroup $fullWidth>
              <InputLabel>Primary Name (To Keep)</InputLabel>
              <TextField
                value={primaryName}
                onChange={e => setPrimaryName(e.target.value)}
                required
              />
            </InputGroup>

            <InputGroup $fullWidth>
              <InputLabel>
                Secondary Names (To Merge & Delete) - Comma separated
              </InputLabel>
              <TextField
                value={secondaryNamesStr}
                onChange={e => setSecondaryNamesStr(e.target.value)}
                required
              />
            </InputGroup>

            <FormActionRow>
              <Md3FilledButton type="submit">Merge Metadata</Md3FilledButton>
              {mergeMessage && <MessageText>{mergeMessage}</MessageText>}
            </FormActionRow>
          </MergeForm>
        </AccordionDetails>
      </Accordion>

      <Accordion>
        <AccordionSummary>
          Assigned Books
          <ExpandMoreIcon />
        </AccordionSummary>
        <AccordionDetails>
          <BooksGrid>
            {assignedBooks.map(book => (
              <BookCard key={book.bookid} book={book} />
            ))}
            {assignedBooks.length === 0 && (
              <EmptyMessage>No books are currently assigned.</EmptyMessage>
            )}
          </BooksGrid>
        </AccordionDetails>
      </Accordion>
    </Container>
  );
}
